import React from 'react';

import { Alert, CircularProgress } from '@mui/material';

import { useFlashAvailability } from '~/hooks/apis/calendar/useFlashAvailability';

import { FlashPackageStay } from '~/services/FlashCalendarService';
import { formatDateWeekDayShortMonth } from '~/services/TimeService';

import { checkInDate, hotelStatus } from './hotelStay';

interface Props {
  stay: FlashPackageStay;
  dayOffset: number;
  departureDates: Array<string>;
}

const MAX_LISTED = 5;

/** Checks the hotel against every departure the bundle sells, before it is added. */
export default function HotelAvailabilitySummary({ stay, dayOffset, departureDates }: Props) {
  const checkIns = departureDates.map((date) => checkInDate(date, dayOffset));
  const { data: availability, isFetching, error } = useFlashAvailability(stay, checkIns);

  if (departureDates.length === 0) return null;

  if (isFetching) {
    return (
      <Alert severity="info" icon={<CircularProgress size={18} />}>
        Checking the hotel's availability for {departureDates.length} departures…
      </Alert>
    );
  }

  if (error) {
    return <Alert severity="warning">Couldn't check the hotel's availability: {error.message}</Alert>;
  }

  const unavailable = [...departureDates]
    .sort()
    .filter((date) => hotelStatus(availability, checkInDate(date, dayOffset)) !== 'available');

  if (unavailable.length === 0) {
    return <Alert severity="success">Available for single and double on all {departureDates.length} departures.</Alert>;
  }

  const soldOut = unavailable.filter((date) => hotelStatus(availability, checkInDate(date, dayOffset)) === 'soldOut');
  const reasons = [
    soldOut.length > 0 && `sold out on ${soldOut.length}`,
    soldOut.length < unavailable.length && `no rates for ${unavailable.length - soldOut.length}`,
  ]
    .filter(Boolean)
    .join(', ');
  const listed = unavailable
    .slice(0, MAX_LISTED)
    .map((date) => formatDateWeekDayShortMonth(checkInDate(date, dayOffset)))
    .join(', ');
  return (
    <Alert severity="warning">
      Can't be sold on {unavailable.length} of {departureDates.length} departures: {reasons} (check-in {listed}
      {unavailable.length > MAX_LISTED ? `, and ${unavailable.length - MAX_LISTED} more` : ''}). You can still add it;
      those departures are flagged on the bundle.
    </Alert>
  );
}
